import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect} from 'react';

import * as OnboardingActions from 'actions/OnboardingActions';
import ChecklistItem from 'onboarding/ChecklistItem';
import {ANALYTICS_LABELS, OnboardingTask} from 'onboarding/OnboardingConstants';
import {FONT_FAMILY_PROXIMA} from 'styles/FontConstants';
import ProgressBar from 'ui-library/bs/ProgressBar';
import {BLUE_LIGHT, GRAY, WHITE} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {trackClickTask} from './OnboardingUtils';

function CollapsedChecklistButton(props: {module: string; title: string; onClick: () => void}): JSX.Element {
  return (
    <div className={classNames(css(styles.minimizedChecklist), 'u-pointer')}>
      <BenchlingIcon icon="checklist" className="u-mini-spacing-right" onClick={props.onClick} />
      <div onClick={props.onClick}>{props.title}</div>
      <BenchlingIcon
        icon="close"
        className={css(styles.dismissIcon)}
        onClick={() => {
          OnboardingActions.dismissNUEModule(props.module);
        }}
      />
    </div>
  );
}

function ChecklistHeader(props: {
  titleText: string;
  onClickCollapse: () => void;
  percentageComplete: number;
}): JSX.Element {
  return (
    <div className={css(styles.header)}>
      <div className={css(styles.headerText)}>
        {props.titleText}
        <span
          className={classNames('pull-right u-pointer', css(styles.toggleIcon))}
          onClick={props.onClickCollapse}
        >
          <BenchlingIcon icon="chevron-down" />
        </span>
      </div>
      <div className={css(styles.progressContainer)}>
        <ProgressBar className={css(styles.progressBar)} value={props.percentageComplete} type="onboarding" />
        <div className={css(styles.percentText)}> {props.percentageComplete}% </div>
      </div>
    </div>
  );
}

interface ChecklistItemListingProps {
  title: string;
  listItems: Array<OnboardingTask>;
  module: string;
  openIndex: number | null;
  collapsed: boolean;
  completed: boolean;
  onChangeCollapsed: (isCollapsed: boolean) => void;
  fixedSize: boolean;
  collapsedNode?: JSX.Element;
}

export default function ChecklistItemListing(props: ChecklistItemListingProps): JSX.Element {
  const {completed, module} = props;
  useEffect(() => {
    if (completed) {
      OnboardingActions.acknowledgeCompletedModule(module);
    }
  }, [completed, module]);

  const toggleOpen = (index: number): void => {
    OnboardingActions.setNUEModuleOpenIndex(
      props.module,
      props.openIndex === index && !props.collapsed ? null : index
    );
  };

  const handleChangeCollapsed = (): void => {
    props.onChangeCollapsed(!props.collapsed);
    OnboardingActions.toggleCollapsedNUEModule(props.module, !props.collapsed);
  };
  const handleClickModuleItem = (index: number): void => {
    const item = props.listItems[index];
    if (!item.description && !item.steps && !item.onClick) {
      return;
    }
    if (item.onClick) {
      item.onClick();
    }
    trackClickTask(
      index + 1,
      item.title,
      props.openIndex !== index,
      ANALYTICS_LABELS[module].key,
      ANALYTICS_LABELS[module].title,
      props.listItems.length,
      item.resultForTracking
    );
    toggleOpen(index);
  };
  const calculatePercentageComplete = (): number => {
    const eligibleItems = props.listItems.filter((item) => item.countTowardCompletion);
    return Math.ceil((eligibleItems.filter((item) => item.completed).length / eligibleItems.length) * 100);
  };
  const collapsedNode = props.collapsedNode ?? (
    <CollapsedChecklistButton module={props.module} title={props.title} onClick={handleChangeCollapsed} />
  );

  return (
    <>
      {props.collapsed ? (
        collapsedNode
      ) : (
        <div className={css(styles.checklist, props.fixedSize && styles.fixedSizeChecklist)}>
          <ChecklistHeader
            titleText={props.title}
            onClickCollapse={handleChangeCollapsed}
            percentageComplete={calculatePercentageComplete()}
          />
          {!props.collapsed && <div className={css(styles.verticalConnector)} />}
          {props.listItems.map((item, i) => {
            const isLast = i === props.listItems.length - 1;
            // A row is actionable only if pressing it does something: reveals content or runs an action.
            // This mirrors the early return in `handleClickModuleItem` and excludes the inert reward row.
            const isActionable = Boolean(item.description || item.steps || item.onClick);
            return (
              <ChecklistItem
                key={item.key}
                title={item.title}
                completed={item.completed}
                description={item.description}
                steps={item.steps}
                isOpen={i === props.openIndex}
                onClick={() => {
                  handleClickModuleItem(i);
                }}
                isActionable={isActionable}
                isLast={isLast}
              />
            );
          })}
        </div>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  minimizedChecklist: {
    height: '32px',
    width: 'max-content',
    borderRadius: '100px',
    padding: '8px 10px 8px 10px',
    backgroundColor: BLUE_LIGHT,
    color: WHITE,
    fontSize: '12px',
    lineHeight: '18px',
    display: 'flex',
    alignItems: 'center',
  },
  dismissIcon: {
    width: '.75em',
    height: '.75em',
    marginLeft: '6px',
  },
  checklist: {
    borderRadius: '4px',
    border: `2px solid ${BLUE_LIGHT}`,
    boxShadow: '0px 0px 6px 3px rgba(52, 64, 67, 0.2)',
    backgroundColor: WHITE,
    paddingBottom: '4px',
  },
  fixedSizeChecklist: {
    minHeight: '360px',
    width: '270px',
  },
  header: {
    height: '66px',
    padding: '9px, 11px, 9px, 11px',
    backgroundColor: BLUE_LIGHT,
  },
  headerText: {
    color: WHITE,
    fontFamily: FONT_FAMILY_PROXIMA,
    fontSize: '16px',
    lineHeight: '24px',
    paddingTop: '10px',
    paddingLeft: '10px',
  },
  toggleIcon: {
    paddingRight: '13px',
  },
  progressContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  progressBar: {
    height: '8px',
    width: '80%',
    marginTop: '13px',
    marginLeft: '10px',
  },
  percentText: {
    marginRight: '10px',
    marginTop: '8px',
    color: WHITE,
  },
  verticalConnector: {
    height: 'calc(100% - 110px)',
    position: 'absolute',
    left: '22px',
    top: '85px',
    borderLeft: `2px solid ${GRAY}`,
  },
});
